import {
  Box,
  Button,
  ColumnFilterPopover,
  DRIVES_PER_PAGE,
  FilterDropDown,
  H2,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  ListDriveInfo,
  ListSensorInfo,
  MRT_PaginationState,
  MRT_Row,
  MRT_SortingState,
  MRT_TableOptions,
  RecordingProcessStatus,
  RowActionButton,
  PartnerRecording,
  TextInput,
  BaseRecordingTableColumn,
  baseRecordingColumnMapping,
  ModalContainer,
  useDisclosure,
} from '@ouster/webviz-react-components';

import { IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MdClear, MdRefresh, MdSearch } from 'react-icons/md';
import { TbEyeStar } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import PublicPartnerPreviewContent from '../../components/publicSample/PublicPartnerPreviewContent';
import RecordingAccessConfig from '../../components/recordingTable/RecordingAccessConfig';
import RecordingDownloadPopover from '../../components/recordingTable/RecordingDownloadPopover';
import { useColumnVisibility } from '../../components/recordingTable/useColumnVisibility';
import useFilters from '../../components/recordingTable/useFilters';
import useOrderBy from '../../components/recordingTable/useOrderBy';
import { useSharedFromOusterListQuery } from '../../components/recordingTable/useRecordingListQuery';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions, useStoreState } from '../../reducers';
import { RecordingPublicityAccessConfig } from '../../types';
import { FILTER_GROUPS } from '../../utils/constants';
import { convertFilterArrayToObject } from '../../utils/driveUtil';
import { createPaginationConfig } from '../../utils/tableUtils';
import demoStyles from '../public/inAppDemo.module.css';

const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  sdk_prod_sn: false,
  sdk_prod_hw_rev: false,
};

const saveSearchToLocalStorage = (search: string, orgId: number) => {
  const searchKey = `partner_preview_${orgId}`;
  localStorage.setItem(searchKey, search);
};

type RowActionsProps = {
  row: MRT_Row<ListDriveInfo>;
  copyCloudVizLink: (value: ListDriveInfo) => void;
  configRecordingPublicity: (value: ListDriveInfo) => void;
  openRecordingDownloadPopover: (
    row: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void;
};

const RowActions = ({
  row,
  copyCloudVizLink,
  configRecordingPublicity,
  openRecordingDownloadPopover,
}: RowActionsProps) => {
  const { curUserOrg, isOusterUser } = useStoreState(
    (storeState) => storeState.user
  );

  return (
    <Box
      flexWrap="nowrap"
      display="flex"
      data-testid="partner-row-action-buttons"
    >
      {/* 
        only Ouster admin can configure sample recording
      */}
      {isOusterUser && curUserOrg?.role === 'ADMIN' && (
        <RowActionButton
          title="Configure Sample Access"
          icon={<TbEyeStar />}
          row={row.original}
          onClickHandler={configRecordingPublicity}
        />
      )}
      <RowActionButton
        title="Copy the visualization link"
        icon={<Icon icon="share" />}
        row={row.original}
        onClickHandler={copyCloudVizLink}
      />
      <RowActionButton
        title="Download recording"
        icon={<Icon icon="download" />}
        row={row.original}
        onClickHandler={openRecordingDownloadPopover}
      />
    </Box>
  );
};

export default function PrivatePartnerPreview() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { showToast } = useChakraHook();

  const { isOusterUser, curUserOrg } = useStoreState(
    (storeState) => storeState.user
  );

  // download api
  const { setSourceFileInfo, setOutputFileInfo, updateNewContentStatus } =
    useStoreActions((actions) => actions.drive);

  // filter values
  const { loadSharedFromOusterFilterOptions } = useStoreActions(
    (actions) => actions.driveList
  );
  const { sharedFromOusterFilterOptions } = useStoreState((storeState) => ({
    ...storeState.driveList,
  }));
  const filterOptions = convertFilterArrayToObject(
    sharedFromOusterFilterOptions
  );

  // table sorting
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: 'shared_time_stamp', desc: true },
  ]);

  // table manual pagination
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: DRIVES_PER_PAGE,
  });
  const tableContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tableContainer = tableContainerRef.current?.querySelector(
      '.MuiTableContainer-root'
    ) as HTMLElement;
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  }, [pagination.pageIndex]);

  const { orderBy, sortOrder, setOrderBy } = useOrderBy();
  const { filters, setGroupFilters } = useFilters();
  const [searchTerm, setSearchTerm] = useState('');

  const { data, meta, isError, isRefetching, isLoading, isSuccess, refetch } =
    useSharedFromOusterListQuery(
      curUserOrg,
      orderBy,
      sortOrder,
      pagination.pageIndex,
      pagination.pageSize,
      location.search
    );

  const { columnVisibility, setColumnVisibility } = useColumnVisibility({
    orgId: curUserOrg?.id,
    defaultVisibility: DEFAULT_COLUMN_VISIBILITY,
    configKey: 'share_from_ouster_column_config',
  });

  const copyToClipboard = async (text: string, type = 'Link') => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: `${type} copied to clipboard`,
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyCloudVizLink = (recording: ListDriveInfo) => {
    const url = `/organization/${recording.organization_id}/drive/${recording.id}`;

    copyToClipboard(`${window.location.origin}${url}`);
  };

  const {
    isOpen: isPublicitySettingOpen,
    onOpen: onPublicitySettingOpen,
    onClose: onPublicitySettingClose,
  } = useDisclosure();
  const publicityRecordingRef = useRef<
    RecordingPublicityAccessConfig | undefined
  >(undefined);

  const onPublicitySettingCloseHandler = useCallback(() => {
    publicityRecordingRef.current = undefined;

    if (localStorage.getItem('recordingAccessConfigChanged')) {
      queryClient.invalidateQueries({ queryKey: ['drive-list'] });
      queryClient.invalidateQueries({
        queryKey: ['private-public-drive-list'],
      });

      queryClient.refetchQueries({
        queryKey: ['public-public-drive-list'],
      });

      refetch();

      localStorage.removeItem('recordingAccessConfigChanged');
    }

    onPublicitySettingClose();
  }, [onPublicitySettingClose, queryClient, refetch]);

  const configRecordingPublicity = (recording: ListDriveInfo) => {
    publicityRecordingRef.current = {
      organization_id: recording.organization_id,
      id: Number(recording.id),
      publication_uuid: recording.publication_uuid,
      data_category: recording.data_category,
      sensor_info: recording.sensor_info,
      start_time: recording.start_time,
      recording_title: recording.recording_title,
    };
    onPublicitySettingOpen();
  };

  /* 
    Click on download button will triggered a popover with all downloadable links
  */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [downloadRecordingId, setDownloadRecordingId] = useState('');
  const downloadRecordingOrgIdRef = useRef<number | undefined>(undefined);
  const downloadRecordingTitleRef = useRef<string | undefined>(undefined);

  const openRecordingDownloadPopover = (
    recording: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (!event || !recording.id) return;
    setAnchorEl(event.currentTarget);
    setDownloadRecordingId(recording.id.toString());
    downloadRecordingOrgIdRef.current = recording.organization_id ?? undefined;
    downloadRecordingTitleRef.current = recording.recording_title ?? undefined;
  };

  const closeDownloadMenu = () => {
    setOutputFileInfo([]);
    setSourceFileInfo([]);
    setAnchorEl(null);
  };

  const customTableOptions: Partial<MRT_TableOptions<ListDriveInfo>> = {
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    renderRowActions: ({ row }) => (
      <RowActions
        row={row}
        copyCloudVizLink={copyCloudVizLink}
        configRecordingPublicity={configRecordingPublicity}
        openRecordingDownloadPopover={openRecordingDownloadPopover}
      />
    ),
    enableColumnPinning: false,
    enableHiding: true,
    enableTopToolbar: true,
    rowCount: meta?.totalCount ?? 0,
    state: {
      isLoading,
      pagination,
      showLoadingOverlay: false,
      showAlertBanner: isError,
      showProgressBars: isLoading || isRefetching,
      showSkeletons: isLoading || isRefetching,
      sorting,
      columnVisibility,
    },
    ...createPaginationConfig(meta, pagination),
  };

  useEffect(() => {
    if (sorting.length) {
      const { id: accessorKey, desc } = sorting[0];
      if (
        accessorKey ===
        baseRecordingColumnMapping[BaseRecordingTableColumn.UPLOADED]
      ) {
        const sortDirection = desc ? 'desc' : 'asc';

        setOrderBy(accessorKey, sortDirection);
      }
    }
  }, [setOrderBy, sorting]);

  const [isDefaultQueryReady, setIsDefaultQueryReady] = useState(false);
  useLayoutEffect(() => {
    if (location.search === '') {
      navigate(
        { search: 'order_by=shared_time_stamp&sort_order=desc' },
        { replace: true }
      );
    } else {
      setIsDefaultQueryReady(true);
    }
  }, [location.search, navigate]);
  useEffect(() => {
    if (
      curUserOrg?.id &&
      isDefaultQueryReady &&
      isSuccess &&
      !sessionStorage.getItem('orgChanged')
    ) {
      saveSearchToLocalStorage(location.search, curUserOrg.id);
    }
  }, [isDefaultQueryReady, isSuccess, location.search, curUserOrg?.id]);

  useEffect(() => {
    if (location.search !== '?order_by=shared_time_stamp&sort_order=desc')
      return;
    const searchKey = `partner_preview_${curUserOrg?.id}`;
    const storedSearch = localStorage.getItem(searchKey);

    if (storedSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: storedSearch,
        },
        { replace: true }
      );
    }
  }, [navigate, location.pathname, location.search, curUserOrg?.id]);

  /* 
    Whenever user switch organization. We need to clear the previous filters and invalidate query
  */
  useEffect(() => {
    if (curUserOrg?.id && sessionStorage.getItem('orgChanged')) {
      queryClient.invalidateQueries({ queryKey: ['shared-from-list'] });
      // get search query based on orgId
      const storedSearch = localStorage.getItem(
        `partner_preview_${curUserOrg.id}`
      );

      navigate({
        pathname: location.pathname,
        search: storedSearch || '',
      });
      sessionStorage.removeItem('orgChanged');
    }
  }, [curUserOrg?.id, location.pathname, navigate, queryClient]);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadSharedFromOusterFilterOptions(curUserOrg.id);
    }
  }, [curUserOrg?.id, loadSharedFromOusterFilterOptions]);

  // Custom in page filter function
  const filterFields = useMemo(
    () => [
      baseRecordingColumnMapping[BaseRecordingTableColumn.STATUS],
      baseRecordingColumnMapping[BaseRecordingTableColumn.DUR],
      baseRecordingColumnMapping[BaseRecordingTableColumn.PRODUCT_TYPE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RESOLUTION],
      baseRecordingColumnMapping[BaseRecordingTableColumn.BEAM_SPACING],
      baseRecordingColumnMapping[BaseRecordingTableColumn.MODE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.FW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.HW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.SN],
      baseRecordingColumnMapping[BaseRecordingTableColumn.FW_IMG_REV],
      baseRecordingColumnMapping[BaseRecordingTableColumn.TAGS],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RECORDING_TITLE],
    ],
    []
  );

  const filteredData = useMemo(() => {
    // start filter when search term's length is greater than 2
    if (!searchTerm || searchTerm.length <= 2) return data;

    const lowercasedSearchTerm = searchTerm.toLowerCase();

    /* 
      For multi-sensor support, the sensor information are moved to sensor array
      Hence, to filter the data, we need to go deep and check the sensor array as well.
    */
    return data.filter((item) =>
      filterFields.some((field) => {
        if (item.sensor_info[0] && field in item.sensor_info[0]) {
          // Field belongs to ListSensorInfo, check inside sensor_info
          return item.sensor_info?.some((sensor) =>
            sensor[field as keyof ListSensorInfo]
              ?.toString()
              .toLowerCase()
              .includes(lowercasedSearchTerm)
          );
        }
        // Field belongs to ListDriveInfo, check on item
        return item[field as keyof ListDriveInfo]
          ?.toString()
          .toLowerCase()
          .includes(lowercasedSearchTerm);
      })
    );
  }, [data, filterFields, searchTerm]);

  // column filter component
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedColKeys, setSelectedColKeys] = useState<string[]>([]);
  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const openVizPage = (recording: ListDriveInfo) => {
    if (isLoading) return;

    if (
      recording.status !== RecordingProcessStatus.Processing &&
      curUserOrg?.id
    ) {
      updateNewContentStatus({
        organizationId: curUserOrg.id,
        viewType: 'SPECIFIC_ORGS',
      });

      const url = `/organization/${recording.organization_id}/drive/${recording.id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const clearAllFilters = () => {
    if (curUserOrg?.id) {
      localStorage.removeItem(`share_search`);
    }

    navigate({
      pathname: location.pathname,
      search: '', // Clear search parameters
    });
  };

  const totalSelectedFilters = Object.values(filters).reduce(
    (total, filterValues) => total + filterValues.length,
    0
  );

  if (!curUserOrg) return null;

  if (isSuccess && data?.length === 0 && isEmpty(filters))
    return (
      <Box
        className={`${demoStyles['page-container']}`}
        data-testid="partner-preview-empty"
      >
        <H2 fontWeight="medium" fontSize={30}>
          Exclusive Previews for Ouster Partners
        </H2>
        <PublicPartnerPreviewContent isPrivate />
      </Box>
    );

  return (
    <>
      <Box
        p="25px 36px 0px"
        h="100%"
        overflow="hidden"
        position="relative"
        display="grid"
        gridTemplateRows="auto 1fr"
        gap="1rem"
      >
        <Box display="grid" rowGap="1rem">
          <Box
            display="flex"
            placeContent="space-between"
            alignItems="center"
            mb={6}
          >
            <H2 fontWeight="medium" fontSize={30}>
              Exclusive Previews for Ouster Partners
            </H2>
            <Button
              variant="ghost"
              colorScheme="gray"
              gap={1}
              onClick={() => refetch()}
              style={{ height: 'auto' }}
              isLoading={isRefetching}
              data-testid="partner-preview-refresh"
            >
              <MdRefresh size={18} /> Refresh List
            </Button>
          </Box>
          {/* filters and search controls */}
          <Box
            display="flex"
            gap={2}
            alignItems="center"
            flexWrap="wrap"
            mr="50px"
            zIndex={2}
            data-testid="partner-preview-filters"
          >
            <InputGroup maxW="25ch" data-testid="partner-preview-search-group">
              <InputLeftElement pointerEvents="none">
                <MdSearch size={18} />
              </InputLeftElement>
              <TextInput
                pl="2.5rem"
                pr="3.5rem"
                placeholder="Search ..."
                type="search"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                data-testid="partner-preview-search-input"
              />
              <InputRightElement>
                <IconButton
                  onClick={() => setSearchTerm('')}
                  data-testid="partner-preview-search-clear"
                >
                  <MdClear size={16} />
                </IconButton>
              </InputRightElement>
            </InputGroup>

            {FILTER_GROUPS.map((groupedFilter) => (
              <FilterDropDown
                title={groupedFilter.title}
                filterKey={groupedFilter.filterKeys}
                filterOptions={filterOptions}
                setGroupFilters={setGroupFilters}
                selectedFilters={filters}
                key={groupedFilter.title}
                data-testid={`partner-preview-filter-${groupedFilter.title
                  .toLowerCase()
                  .replace(/\s/g, '-')}`}
              />
            ))}

            {totalSelectedFilters > 0 && (
              <Button
                variant="ghost"
                colorScheme="gray"
                gap={1}
                onClick={() => clearAllFilters()}
                fontSize="12px"
                data-testid="partner-preview-clear-filters"
              >
                <MdClear size={18} /> Clear Filters
              </Button>
            )}
          </Box>
        </Box>

        <Box
          h="100%"
          overflow="hidden"
          position="relative"
          bg="rgb(29, 29, 29)"
          mt="-56px"
          zIndex={1}
          ref={tableContainerRef}
          data-testid="partner-preview-table-container"
        >
          <PartnerRecording
            key={curUserOrg?.id}
            filters={{}}
            maxHeight="calc(100vh - 290px)"
            filteredData={filteredData}
            setFilterAnchorEl={setFilterAnchorEl}
            setSelectedColKeys={setSelectedColKeys}
            openVizPage={openVizPage}
            customTableOptions={customTableOptions}
            isOuster={isOusterUser}
          />
        </Box>
      </Box>
      {anchorEl && (
        <RecordingDownloadPopover
          anchorEl={anchorEl}
          closeDownloadMenu={closeDownloadMenu}
          downloadRecordingId={downloadRecordingId}
          orgId={downloadRecordingOrgIdRef.current}
          recordingTitle={downloadRecordingTitleRef.current}
        />
      )}

      {filterAnchorEl && (
        <ColumnFilterPopover
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilterPopover}
          filterKey={selectedColKeys}
          filterOptions={filterOptions}
          selectedFilters={filters}
          setGroupFilters={setGroupFilters}
        />
      )}

      {publicityRecordingRef.current && (
        <ModalContainer
          isOpen={isPublicitySettingOpen}
          onClose={onPublicitySettingCloseHandler}
          header="Recording Access Configuration"
          footer={null}
          content={
            <RecordingAccessConfig
              recording={publicityRecordingRef.current}
              onClose={onPublicitySettingCloseHandler}
            />
          }
        />
      )}
    </>
  );
}
